// Default import, not `import * as`: loglevel is CommonJS, and a namespace import resolves
// to an interop copy under some transforms. Patching methodFactory on a copy silently does
// nothing, because getLogger reads the factory off the real module object.
import log from 'loglevel'; // npmjs.com/package/loglevel
import env from '@beam-australia/react-env';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import type { ITelemetryItem } from '@microsoft/applicationinsights-web';
import reactAppEnv from 'utils/reactAppEnv';

// Derived from loglevel's own signatures rather than imported, so they stay correct if the
// package's types change.
type LogLevelNumbers = Parameters<typeof log.methodFactory>[1];
type Logger = ReturnType<typeof log.getLogger>;

// Domains whose dependency telemetry is worth ingesting. Everything else the App Insights
// ajax/fetch tracker picks up — marketing tags injected by Ensighten, ad and analytics
// vendors, LaunchDarkly polling, Glassbox, Sentry — is dropped before it is billed.
const FIRST_PARTY_DEPENDENCY_DOMAINS = [
  'rac.co.uk',
  'contentstack.io',
  'insure-systems.co.uk',
  'azure-api.net',
  'web.core.windows.net',
];

const REMOTE_DEPENDENCY_BASE_TYPE = 'RemoteDependencyData';

const getAppInsightLogLevel = (loglevel: LogLevelNumbers): SeverityLevel | undefined => {
  switch (loglevel) {
    case log.levels.TRACE:
    case log.levels.DEBUG:
    case log.levels.INFO:
      return SeverityLevel.Information;
    case log.levels.WARN:
      return SeverityLevel.Warning;
    case log.levels.ERROR:
      return SeverityLevel.Error;
    case log.levels.SILENT:
    default:
      return undefined;
  }
};

// target is the request host, e.g. 'www.rac.co.uk' or 'localhost:3000'. Matching on a
// domain boundary rather than a substring so 'rac.co.uk.example.com' is not treated as ours.
const isFirstPartyDependency = (target: unknown): boolean => {
  const host = String(target).split(':')[0].toLowerCase();
  return FIRST_PARTY_DEPENDENCY_DOMAINS.some(
    (domain) => host === domain || host.endsWith(`.${domain}`),
  );
};

// Anything we cannot classify is kept — losing real signal is worse than ingesting a
// little noise.
const filterThirdPartyDependencies = (item: ITelemetryItem): boolean => {
  if (item?.baseType !== REMOTE_DEPENDENCY_BASE_TYPE) {
    return true;
  }
  const target = item?.baseData?.target;
  if (!target) {
    return true;
  }
  return isFirstPartyDependency(target);
};

// One instance per page. loadAppInsights() appends a hook to the shared
// XMLHttpRequest/fetch instrumentation rather than replacing it, so a second instance
// would emit a duplicate record for every request the app makes.
let appInsights: ApplicationInsights | null | undefined;

const getAppInsights = (): ApplicationInsights | null => {
  if (appInsights !== undefined) {
    return appInsights;
  }
  const key = env('APP_INSIGHT_KEY');
  if (!key) {
    appInsights = null;
    return appInsights;
  }
  appInsights = new ApplicationInsights({
    config: {
      instrumentationKey: key,
      // Applied by the sender to every telemetry type, dependencies included.
      samplingPercentage: 10,
    },
  });
  appInsights.loadAppInsights();
  appInsights.addTelemetryInitializer(filterThirdPartyDependencies);
  appInsights.trackPageView();
  return appInsights;
};

let isMethodFactoryPatched = false;

// Patched once. Each patch wraps the previous one, so patching per logger made a single
// logger.warn() emit one trackTrace per logger that had been created.
const patchLogMethodFactory = (insights: ApplicationInsights): void => {
  if (isMethodFactoryPatched) {
    return;
  }
  isMethodFactoryPatched = true;
  const originalFactory = log.methodFactory;
  log.methodFactory = (methodName, logLev, loggerName) => {
    const rawMethod = originalFactory(methodName, logLev, loggerName);
    const appInsightLoglevel = getAppInsightLogLevel(logLev);
    if (!appInsightLoglevel) {
      return rawMethod;
    }
    return (message: string) => {
      rawMethod(message);
      insights.trackTrace({
        message,
        severityLevel: appInsightLoglevel,
      });
    };
  };
};

const appInsightLogger = (logLevel: LogLevelNumbers): void => {
  const insights = getAppInsights();
  if (!insights) {
    log.setLevel(log.levels.SILENT, false);
    return;
  }
  patchLogMethodFactory(insights);
  log.setLevel(logLevel, false);
};

const loggerFunc = (loggerName: string): Logger => {
  let logLevel: LogLevelNumbers;
  let sendToAppInsights = false;
  switch (reactAppEnv()) {
    case 'dev':
    case 'local':
    case 'sit':
    case 'pre':
      // Logs that contain the most detailed messages and may contain sensitive application data (should never be enabled in a production environment)
      logLevel = log.levels.TRACE;
      sendToAppInsights = true;
      break;
    case 'prd':
      // Logs that track the general flow of the application
      logLevel = log.levels.WARN;
      sendToAppInsights = true;
      break;
    case 'uat':
      // Logging disabled
      logLevel = log.levels.TRACE;
      break;
    default:
      logLevel = log.levels.TRACE;
      break;
  }
  // Before getLogger: loglevel snapshots log.methodFactory when a named logger is
  // created, so a logger built first would never report to App Insights.
  if (sendToAppInsights) {
    appInsightLogger(logLevel);
  }
  const logging = log.getLogger(loggerName);
  logging.setLevel(logLevel, false);
  return logging;
};

// eslint-disable-next-line import/prefer-default-export
export { loggerFunc };
